import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../../config/api';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { useAuth } from '../../../context/AuthContext';
import {
  ArrowLeft,
  Building2,
  Mail,
  Phone,
  Globe,
  MapPin,
  User,
  CreditCard,
  Banknote,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  TrendingUp,
  Calendar
} from 'lucide-react';

interface Registration {
  [key: string]: any;
}

const RegistrationDetailPageModern: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [registration, setRegistration] = useState<Registration | null>(null);
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Status update form
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');
  const [requestedSections, setRequestedSections] = useState<string[]>([]);
  const [isUpdating, setIsUpdating] = useState(false);

  // Bank account visibility toggle
  const [showFullBankAccount, setShowFullBankAccount] = useState(false);

  useEffect(() => {
    if (id) {
      fetchRegistrationDetails();
    }
  }, [id]);

  const fetchRegistrationDetails = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.get(`/admin/registrations/${id}`);

      if (response.data.success) {
        setRegistration(response.data.data.registration);
        setAuditLogs(response.data.data.auditLogs || []);
      } else {
        setError('Failed to fetch registration details');
      }
    } catch (err: any) {
      console.error('Error fetching registration details:', err);
      setError(err.response?.data?.error?.message || 'Failed to load registration');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!selectedStatus) {
      alert('Please select a status');
      return;
    }

    // Validate that user is authenticated with a valid ID
    if (!user?.id) {
      alert('You must be logged in to update registration status. Please refresh and try again.');
      return;
    }

    setIsUpdating(true);

    try {
      const response = await api.put(`/admin/registrations/${id}/status`, {
        status: selectedStatus,
        approvalNotes,
        reviewedBy: user.id,
        ...(selectedStatus === 'changes_requested' && requestedSections.length > 0
          ? { requestedSections }
          : {}),
      });

      if (response.data.success) {
        alert(`Registration ${selectedStatus} successfully!`);
        setShowStatusModal(false);
        setSelectedStatus('');
        setApprovalNotes('');
        setRequestedSections([]);
        fetchRegistrationDetails();
      }
    } catch (err: any) {
      console.error('Error updating status:', err);
      const errorMessage = err.response?.data?.error?.message || 'Failed to update status';

      // Handle specific error cases
      if (err.response?.status === 409) {
        alert(`${errorMessage}\n\nThis registration may have been updated by another user. Please refresh the page.`);
        fetchRegistrationDetails(); // Refresh to show latest state
      } else if (err.response?.status === 400) {
        alert(`Validation error: ${errorMessage}`);
      } else {
        alert(errorMessage);
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const configs: { [key: string]: { label: string; icon: any; gradient: string; bg: string; iconColor: string } } = {
      pending: {
        label: 'Pending Review',
        icon: Clock,
        gradient: 'from-yellow-500 to-orange-600',
        bg: 'from-yellow-50 to-orange-50',
        iconColor: 'text-orange-600'
      },
      approved: {
        label: 'Approved',
        icon: CheckCircle,
        gradient: 'from-green-500 to-emerald-600',
        bg: 'from-green-50 to-emerald-50',
        iconColor: 'text-green-600'
      },
      rejected: {
        label: 'Rejected',
        icon: XCircle,
        gradient: 'from-red-500 to-rose-600',
        bg: 'from-red-50 to-rose-50',
        iconColor: 'text-red-600'
      },
      changes_requested: {
        label: 'Changes Requested',
        icon: AlertCircle,
        gradient: 'from-blue-500 to-indigo-600',
        bg: 'from-blue-50 to-indigo-50',
        iconColor: 'text-blue-600'
      }
    };

    const config = configs[status] || configs['pending'];
    const Icon = config.icon;

    return (
      <div className={`inline-flex items-center px-4 py-2 rounded-xl bg-gradient-to-r ${config.bg} border border-gray-200 shadow-md`}>
        <Icon className={`w-5 h-5 mr-2 ${config.iconColor}`} strokeWidth={2.5} />
        <span className="font-semibold text-gray-900">{config.label}</span>
      </div>
    );
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-96">
          <div className="text-center animate-fadeIn">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-primary shadow-glow-lg mb-6">
              <svg className="animate-spin h-10 w-10 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
            <p className="text-lg text-gray-600 font-medium">Loading registration details...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error || !registration) {
    return (
      <AdminLayout>
        <div>
          <div className="bg-red-50 border-l-4 border-red-500 px-6 py-4 rounded-xl shadow-md">
            <div className="flex items-center">
              <XCircle className="w-5 h-5 text-red-500 mr-3" />
              <span className="text-red-700 font-medium">{error || 'Registration not found'}</span>
            </div>
          </div>
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="mt-6 inline-flex items-center text-primary-600 hover:text-primary-900 font-semibold group"
          >
            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
            Back to Dashboard
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
          {/* Header Card */}
          <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg border border-gray-100 p-6 animate-fadeIn">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="flex-1">
                <button
                  onClick={() => navigate('/admin/dashboard')}
                  className="inline-flex items-center text-sm text-primary-600 hover:text-primary-900 font-semibold mb-3 group"
                >
                  <ArrowLeft className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform" />
                  Back to Dashboard
                </button>
                <div className="flex items-center space-x-3 mb-2">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-primary shadow-lg">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h1 className="text-3xl font-display font-bold text-gray-900">
                      {registration.registration_number}
                    </h1>
                    <p className="text-sm text-gray-600 flex items-center mt-1">
                      <Building2 className="w-4 h-4 mr-1" />
                      {registration.registration_type.charAt(0).toUpperCase() + registration.registration_type.slice(1)} Registration
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-end space-y-3">
                {getStatusBadge(registration.status)}
                {(registration.status === 'pending' || registration.status === 'changes_requested') && (
                  <button
                    onClick={() => setShowStatusModal(true)}
                    className="inline-flex items-center px-6 py-3 bg-gradient-primary text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
                  >
                    <TrendingUp className="w-4 h-4 mr-2" />
                    Update Status
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Main Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Details */}
            <div className="lg:col-span-2 space-y-6">
              {/* Company Information */}
              <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg border border-gray-100 p-6 animate-slideInRight">
                <div className="flex items-center mb-4">
                  <Building2 className="w-5 h-5 text-primary-600 mr-2" />
                  <h2 className="text-xl font-semibold text-gray-900">Company Information</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <InfoItem icon={Building2} label="Company Name" value={registration.entity_name} />
                  <InfoItem icon={FileText} label="Business Type" value={registration.business_type} />
                  <InfoItem icon={Mail} label="Email" value={registration.email} />
                  <InfoItem icon={Phone} label="Phone" value={registration.phone} />
                  {registration.mobile && <InfoItem icon={Phone} label="Mobile" value={registration.mobile} />}
                  {registration.website && (
                    <InfoItem
                      icon={Globe}
                      label="Website"
                      value={
                        <a href={registration.website} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-900 underline">
                          {registration.website}
                        </a>
                      }
                    />
                  )}
                </div>
              </div>

              {/* Addresses */}
              <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg border border-gray-100 p-6 animate-slideInRight">
                <div className="flex items-center mb-4">
                  <MapPin className="w-5 h-5 text-primary-600 mr-2" />
                  <h2 className="text-xl font-semibold text-gray-900">Addresses</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <AddressBlock title="Business Address" registration={registration} type="business" />
                  <AddressBlock title="Remittance Address" registration={registration} type="remittance" />
                </div>
              </div>

              {/* Contacts */}
              <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg border border-gray-100 p-6 animate-slideInRight">
                <div className="flex items-center mb-4">
                  <User className="w-5 h-5 text-primary-600 mr-2" />
                  <h2 className="text-xl font-semibold text-gray-900">Contact Information</h2>
                </div>
                <div className="space-y-6">
                  <ContactBlock title="Primary Contact" registration={registration} type="primary" />
                  {registration.secondary_contact_name && (
                    <ContactBlock title="Secondary Contact" registration={registration} type="secondary" />
                  )}
                </div>
              </div>

              {/* Financial Information */}
              <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg border border-gray-100 p-6 animate-slideInRight">
                <div className="flex items-center mb-4">
                  <CreditCard className="w-5 h-5 text-primary-600 mr-2" />
                  <h2 className="text-xl font-semibold text-gray-900">Financial Information</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <InfoItem icon={FileText} label="Tax ID" value={registration.tax_id} />
                  <InfoItem icon={CreditCard} label="Payment Terms" value={registration.payment_terms} />
                  <InfoItem icon={Banknote} label="Currency" value={registration.currency} />
                  {registration.credit_limit && (
                    <InfoItem icon={TrendingUp} label="Credit Limit" value={`$${registration.credit_limit.toLocaleString()}`} />
                  )}
                </div>

                <div className="pt-6 border-t border-gray-200">
                  <div className="flex items-center mb-4">
                    <Banknote className="w-5 h-5 text-primary-600 mr-2" />
                    <h3 className="text-lg font-semibold text-gray-900">Bank Information</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <InfoItem icon={Building2} label="Bank Name" value={registration.bank_name} />
                    <InfoItem icon={User} label="Account Holder" value={registration.bank_account_holder_name} />
                    <div>
                      <InfoItem
                        icon={FileText}
                        label="Account Number"
                        value={showFullBankAccount ? registration.bank_account_number : `****${registration.bank_account_number.slice(-4)}`}
                      />
                      <button
                        onClick={() => setShowFullBankAccount(!showFullBankAccount)}
                        className="mt-2 text-xs text-primary-600 hover:text-primary-700 underline"
                      >
                        {showFullBankAccount ? 'Hide' : 'Show'} full account number
                      </button>
                    </div>
                    {registration.bank_routing_number && (
                      <InfoItem icon={FileText} label="Routing Number" value={registration.bank_routing_number} />
                    )}
                    {registration.bank_swift_code && (
                      <InfoItem icon={FileText} label="SWIFT/BIC Code" value={registration.bank_swift_code} />
                    )}
                    {registration.bank_iban && (
                      <InfoItem icon={FileText} label="IBAN" value={registration.bank_iban} />
                    )}
                    {registration.bank_address && (
                      <InfoItem icon={MapPin} label="Bank Address" value={registration.bank_address} />
                    )}
                  </div>
                </div>
              </div>

              {/* Shipper Details (Supplier Only) */}
              {registration.registration_type === 'supplier' && (registration.shipper_name || registration.freight_terms) && (
                <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg border border-gray-100 p-6 animate-slideInRight">
                  <div className="flex items-center mb-4">
                    <FileText className="w-5 h-5 text-primary-600 mr-2" />
                    <h2 className="text-xl font-semibold text-gray-900">Shipper Details</h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {registration.shipper_name && <InfoItem icon={Building2} label="Shipper Name" value={registration.shipper_name} />}
                    {registration.shipper_account_number && <InfoItem icon={FileText} label="Account Number" value={registration.shipper_account_number} />}
                    {registration.shipper_contact && <InfoItem icon={User} label="Shipper Contact" value={registration.shipper_contact} />}
                    {registration.freight_terms && <InfoItem icon={FileText} label="Freight Terms" value={registration.freight_terms} />}
                  </div>
                </div>
              )}
            </div>

              {/* Applicant Notes & Special Requirements */}
              {(registration.notes || registration.special_requirements) && (
                <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg border border-gray-100 p-6 animate-slideInRight">
                  <div className="flex items-center mb-4">
                    <FileText className="w-5 h-5 text-primary-600 mr-2" />
                    <h2 className="text-xl font-semibold text-gray-900">Notes & Special Requirements</h2>
                  </div>
                  <div className="space-y-4">
                    {registration.notes && (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-700 mb-1">Applicant Notes</h3>
                        <p className="text-sm text-gray-700 leading-relaxed bg-gray-50 rounded-xl p-4 border border-gray-200">{registration.notes}</p>
                      </div>
                    )}
                    {registration.special_requirements && (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-700 mb-1">Special Requirements</h3>
                        <p className="text-sm text-gray-700 leading-relaxed bg-gray-50 rounded-xl p-4 border border-gray-200">{registration.special_requirements}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

            {/* Right Column - Timeline & Actions */}
            <div className="lg:col-span-1 space-y-6">
              {/* Timeline */}
              <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg border border-gray-100 p-6 animate-fadeIn">
                <div className="flex items-center mb-4">
                  <Clock className="w-5 h-5 text-primary-600 mr-2" />
                  <h2 className="text-lg font-semibold text-gray-900">Activity Timeline</h2>
                </div>
                <div className="flow-root">
                  <ul className="-mb-8">
                    {auditLogs.map((log, idx) => (
                      <li key={log.id}>
                        <div className="relative pb-8">
                          {idx !== auditLogs.length - 1 && (
                            <span className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true"></span>
                          )}
                          <div className="relative flex items-start space-x-3">
                            <div className="relative">
                              <div className="h-10 w-10 rounded-xl bg-gradient-primary flex items-center justify-center shadow-lg">
                                <CheckCircle className="h-5 w-5 text-white" strokeWidth={2.5} />
                              </div>
                            </div>
                            <div className="min-w-0 flex-1">
                              <div>
                                <p className="text-sm font-semibold text-gray-900">
                                  {log.action.replace(/_/g, ' ').charAt(0).toUpperCase() + log.action.replace(/_/g, ' ').slice(1)}
                                </p>
                                {log.user_name && <p className="text-xs text-gray-500 mt-0.5">by {log.user_name}</p>}
                              </div>
                              <div className="mt-1 flex items-center text-xs text-gray-500">
                                <Calendar className="w-3 h-3 mr-1" />
                                {new Date(log.created_at).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Review Notes */}
              {registration.approval_notes && (
                <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg border border-gray-100 p-6 animate-fadeIn">
                  <div className="flex items-center mb-4">
                    <FileText className="w-5 h-5 text-primary-600 mr-2" />
                    <h2 className="text-lg font-semibold text-gray-900">Review Notes</h2>
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed">{registration.approval_notes}</p>
                  {registration.reviewed_by_name && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-xs text-gray-500 flex items-center">
                        <User className="w-3 h-3 mr-1" />
                        Reviewed by {registration.reviewed_by_name}
                      </p>
                      <p className="text-xs text-gray-500 flex items-center mt-1">
                        <Calendar className="w-3 h-3 mr-1" />
                        {new Date(registration.reviewed_at).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

      {/* Status Update Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-6 animate-slideInRight">
            <div className="flex items-center mb-6">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-primary shadow-lg mr-3">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Update Status</h3>
            </div>

            <div className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">New Status</label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500"
                >
                  <option value="">Select status...</option>
                  <option value="approved">✓ Approve</option>
                  <option value="rejected">✗ Reject</option>
                  <option value="changes_requested">⚠ Request Changes</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Notes (Optional)</label>
                <textarea
                  value={approvalNotes}
                  onChange={(e) => setApprovalNotes(e.target.value)}
                  rows={4}
                  placeholder="Add notes about your decision..."
                  className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 resize-none"
                ></textarea>
              </div>

              {selectedStatus === 'changes_requested' && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Sections to Update <span className="text-gray-400 font-normal">(select which areas need changes)</span>
                  </label>
                  <div className="space-y-2 max-h-56 overflow-y-auto">
                    {[
                      { key: 'contact_info', label: 'Phone / Contact Info' },
                      { key: 'primary_contact', label: 'Primary Contact' },
                      { key: 'secondary_contact', label: 'Secondary Contact' },
                      { key: 'business_address', label: 'Business Address' },
                      { key: 'remittance_address', label: 'Remittance Address' },
                      { key: 'financial_info', label: 'Financial Info (Payment Terms / Currency)' },
                      { key: 'bank_details', label: 'Bank Details' },
                      { key: 'business_details', label: 'Business Details' },
                      ...(registration?.registration_type === 'supplier'
                        ? [{ key: 'shipper_details', label: 'Shipper Details' }]
                        : []),
                      { key: 'notes', label: 'Notes / Special Requirements' },
                    ].map((section) => (
                      <label
                        key={section.key}
                        className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          requestedSections.includes(section.key)
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={requestedSections.includes(section.key)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setRequestedSections([...requestedSections, section.key]);
                            } else {
                              setRequestedSections(requestedSections.filter(s => s !== section.key));
                            }
                          }}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <span className="ml-3 text-sm font-medium text-gray-700">{section.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowStatusModal(false);
                  setSelectedStatus('');
                  setApprovalNotes('');
                  setRequestedSections([]);
                }}
                className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
                disabled={isUpdating}
              >
                Cancel
              </button>
              <button
                onClick={handleStatusUpdate}
                disabled={isUpdating || !selectedStatus}
                className="px-6 py-3 bg-gradient-primary text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {isUpdating ? 'Updating...' : 'Update Status'}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </AdminLayout>
  );
};

// Helper Components
const InfoItem: React.FC<{ icon: any; label: string; value: any }> = ({ icon: Icon, label, value }) => (
  <div className="space-y-1">
    <div className="flex items-center text-xs font-medium text-gray-500">
      <Icon className="w-3 h-3 mr-1" />
      {label}
    </div>
    <div className="text-sm font-semibold text-gray-900">{value}</div>
  </div>
);

const AddressBlock: React.FC<{ title: string; registration: any; type: string }> = ({ title, registration, type }) => (
  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
    <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
      <MapPin className="w-4 h-4 mr-1 text-primary-600" />
      {title}
    </h3>
    <p className="text-sm text-gray-700 leading-relaxed">
      {registration[`${type}_address_line1`]}
      {registration[`${type}_address_line2`] && (
        <>
          <br />
          {registration[`${type}_address_line2`]}
        </>
      )}
      <br />
      {registration[`${type}_city`]}, {registration[`${type}_state`]} {registration[`${type}_postal_code`]}
      <br />
      {registration[`${type}_country`]}
    </p>
  </div>
);

const ContactBlock: React.FC<{ title: string; registration: any; type: string }> = ({ title, registration, type }) => (
  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
    <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
      <User className="w-4 h-4 mr-1 text-primary-600" />
      {title}
    </h3>
    <div className="space-y-2">
      <div className="flex items-center text-sm">
        <User className="w-3 h-3 mr-2 text-gray-400" />
        <span className="font-medium text-gray-900">
          {registration[`${type}_contact_name`]}
          {registration[`${type}_contact_title`] && ` - ${registration[`${type}_contact_title`]}`}
        </span>
      </div>
      <div className="flex items-center text-sm">
        <Mail className="w-3 h-3 mr-2 text-gray-400" />
        <span className="text-gray-700">{registration[`${type}_contact_email`]}</span>
      </div>
      <div className="flex items-center text-sm">
        <Phone className="w-3 h-3 mr-2 text-gray-400" />
        <span className="text-gray-700">{registration[`${type}_contact_phone`]}</span>
      </div>
    </div>
  </div>
);

export default RegistrationDetailPageModern;
